import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";

const FALLBACK_IMG = "https://images.pexels.com/photos/842545/pexels-photo-842545.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function SharedRecipe() {
  const { token } = useParams();
  const [r, setR] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/share/${token}`);
        setR(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Link expired or invalid");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-[#5C6B61]">Loading recipe…</div>;
  }
  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[#FAF9F6]">
        <div className="ff-card p-10 max-w-md text-center">
          <div className="ff-label">Share link</div>
          <h1 className="text-2xl font-semibold mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>{err}</h1>
          <p className="text-[#5C6B61] mt-3">Ask the person who shared it to send a fresh link.</p>
          <a href="/" className="ff-btn-primary mt-6 inline-block">Visit Shop, Cal & Meal</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#2A332C]">
      {/* top bar */}
      <header className="border-b border-[#E5E2DA] bg-[#FAF9F6]/95 backdrop-blur sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <a href="/" className="flex items-center gap-2 font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>
            <span className="w-8 h-8 rounded-full bg-[#3B5A3E] text-white flex items-center justify-center">🥗</span>
            Shop, Cal & Meal
          </a>
          <a href="/" data-testid="cta-try-freshfork" className="ff-btn-primary text-sm">Try Shop, Cal & Meal free</a>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 ff-fade-in">
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-8">
          <div className="ff-card overflow-hidden">
            <img
              src={r.image || FALLBACK_IMG}
              alt={r.title}
              onError={(e) => { e.target.src = FALLBACK_IMG; }}
              className="w-full aspect-[4/3] object-cover"
            />
          </div>
          <div>
            <div className="ff-label">Shared by {r.shared_by}</div>
            <h1 data-testid="shared-title" className="text-3xl sm:text-4xl font-semibold tracking-tight mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
              {r.title}
            </h1>
            {r.source_url && (
              <a href={r.source_url} target="_blank" rel="noreferrer"
                 className="text-xs text-[#3B5A3E] hover:underline mt-2 inline-block">source ↗</a>
            )}
            <div className="flex flex-wrap gap-2 mt-4">
              {(r.tags || []).map((t) => <span key={t} className="ff-chip">{t}</span>)}
            </div>
            <div className="grid grid-cols-4 gap-3 mt-6">
              <Stat label="Serves" value={r.servings} />
              <Stat label="Calories" value={r.calories_per_serving ?? "—"} accent />
              <Stat label="Protein" value={r.protein_g != null ? `${r.protein_g}g` : "—"} />
              <Stat label="Carbs" value={r.carbs_g != null ? `${r.carbs_g}g` : "—"} />
            </div>
            <div className="mt-6 p-4 rounded-2xl bg-[#F3F0EA] text-sm text-[#5C6B61]">
              ✨ Want this in <strong className="text-[#2A332C]">your</strong> kitchen with a tap-to-shop basket?
              <a href="/" data-testid="bottom-cta" className="ml-2 font-medium text-[#3B5A3E] underline-offset-4 hover:underline">Save to Shop, Cal & Meal</a>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-8 mt-10">
          <section className="ff-card p-6">
            <div className="ff-label mb-4">Ingredients</div>
            <ul className="space-y-3">
              {(r.ingredients || []).map((i, idx) => (
                <li key={idx} className="flex items-start gap-3 py-2 border-b border-[#E5E2DA] last:border-0">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#3B5A3E]" />
                  <div>
                    <div className="text-sm font-medium">{i.name}</div>
                    {(i.quantity || i.notes) && (
                      <div className="text-xs text-[#8B9690] mt-0.5">
                        {[i.quantity, i.notes].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
          <section className="ff-card p-6">
            <div className="ff-label mb-4">Method</div>
            {r.steps?.length ? (
              <ol className="space-y-4">
                {r.steps.map((s, idx) => (
                  <li key={idx} className="flex gap-4">
                    <span className="flex-none w-8 h-8 rounded-full bg-[#F3F0EA] text-[#3B5A3E] text-sm font-medium flex items-center justify-center">
                      {idx + 1}
                    </span>
                    <p className="text-[#2A332C] leading-relaxed text-[15px]">{s}</p>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-[#8B9690] text-sm">No steps captured — check the source.</p>
            )}
          </section>
        </div>
      </main>

      <footer className="border-t border-[#E5E2DA] mt-16 py-8 text-center text-xs text-[#8B9690]">
        Shared on Shop, Cal & Meal · <a href="/" className="text-[#3B5A3E] hover:underline">Make your own basket</a>
      </footer>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`rounded-2xl p-3 text-center ${accent ? "bg-[#FBEFEA]" : "bg-[#F3F0EA]"}`}>
      <div className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${accent ? "text-[#A65541]" : "text-[#5C6B61]"}`}>{label}</div>
      <div className={`text-lg font-semibold mt-1 ${accent ? "text-[#A65541]" : "text-[#2A332C]"}`} style={{ fontFamily: "Outfit, sans-serif" }}>{value}</div>
    </div>
  );
}
